"use client"

import { Typography } from "@shared/ui.primitives"
import Image from "next/image"
import React, { useEffect, useRef, useState } from "react"
import type { PDPImage } from "@shared/graphql.types"

import * as styles from "./BeforeAfter.css"
import { isDraggable } from "./utils"
import { dispatch } from "@shared/utils.events"

interface Props {
  afterImage?: PDPImage
  beforeImage?: PDPImage
}

const minSliderX = 12
const sliderDefaultValue = 24

export const BeforeAfter: React.FC<Props> = ({ afterImage, beforeImage }) => {
  const beforeAfterRef = useRef<HTMLDivElement>(null)
  const sliderThumbRef = useRef<HTMLDivElement>(null)
  const [sliderValue, setSliderValue] = useState(sliderDefaultValue)
  const [percentageWidth, setPercentageWidth] = useState(50)
  const hasTouchStarted = useRef(false)

  useEffect(() => {
    if (!hasTouchStarted.current) {
      return
    }
    const timer = setTimeout(() => {
      dispatch("page element clicked", {
        module_content: "Clinical Trials Before and After",
        module_content_details: `${sliderValue}%`,
      })
    }, 500)
    return () => clearTimeout(timer)
  }, [sliderValue])

  useEffect(() => {
    let canDrag = false
    const currentRef = beforeAfterRef.current
    const sliderThumb = sliderThumbRef.current

    if (!currentRef || !sliderThumb) return

    const onResize = () => {
      setPercentageWidth(currentRef.offsetWidth)
    }

    window.addEventListener("resize", onResize)
    onResize()

    const onTouchMove = (e: TouchEvent) => {
      const cWidth = currentRef.offsetWidth
      const currentPoint = e.changedTouches[0].clientX

      const startOfDiv = currentRef.offsetLeft

      const modifiedCurrentPoint = currentPoint - startOfDiv

      if (
        modifiedCurrentPoint > minSliderX &&
        modifiedCurrentPoint < cWidth - minSliderX
      ) {
        const pos = (modifiedCurrentPoint * 100) / cWidth
        setSliderValue(pos)
      }
    }

    const onTouchStart = (e: TouchEvent) => {
      canDrag = isDraggable(e, sliderThumb.getBoundingClientRect())
      if (!canDrag) return
      currentRef.style.cursor = "grabbing"
      hasTouchStarted.current = true
      currentRef.addEventListener("touchmove", onTouchMove)
      onTouchMove(e)
    }

    currentRef.addEventListener("touchstart", onTouchStart)

    const onMouseDown = (e: MouseEvent) => {
      canDrag = isDraggable(e, sliderThumb.getBoundingClientRect())
      if (!canDrag) return
      currentRef.style.cursor = "grabbing"
      hasTouchStarted.current = true
      onMouseMove(e)
    }

    const onMouseUp = () => {
      canDrag = false
      currentRef.style.cursor = "grab"
      currentRef.removeEventListener("touchmove", onTouchMove)
    }

    currentRef.addEventListener("mousedown", onMouseDown)

    window.addEventListener("mouseup", onMouseUp)
    window.addEventListener("touchend", onMouseUp)
    window.addEventListener("touchcancel", onMouseUp)

    const onMouseMove = (e: MouseEvent) => {
      if (!canDrag) return
      const cWidth = currentRef.offsetWidth
      const w = (e.offsetX * 100) / cWidth

      if (e.offsetX > minSliderX && e.offsetX < cWidth - minSliderX) {
        setSliderValue(w)
      }
    }

    currentRef.addEventListener("mousemove", onMouseMove)

    return () => {
      currentRef.removeEventListener("mousemove", onMouseMove)
      currentRef.removeEventListener("mousedown", onMouseDown)
      currentRef.removeEventListener("touchstart", onTouchStart)
      currentRef.removeEventListener("touchmove", onTouchMove)
      window.removeEventListener("resize", onResize)
      window.removeEventListener("mouseup", onMouseUp)
      window.removeEventListener("touchend", onMouseUp)
      window.removeEventListener("touchcancel", onMouseUp)
    }
  }, [])

  return (
    <div className={styles.container}>
      <div className={styles.beforeAfterContainer}>
        <div
          data-testid="before-after-container"
          className={styles.beforeAfterBox}
          ref={beforeAfterRef}
        >
          <div
            className={styles.imageContainer}
            style={{ width: `${percentageWidth ?? 100}px` }}
          >
            <Image
              alt="after"
              src={afterImage?.src ?? ""}
              draggable="false"
              fill
            />
            <div className={styles.labelVariants["after"]}>
              <Typography as="p" variant="smallprint-md-bold">
                After
              </Typography>
            </div>
          </div>
          <div
            className={styles.imageContainer}
            style={{ width: `${sliderValue ?? 100}%` }}
          >
            <div
              className={styles.beforeImageContainer}
              style={{
                width: `${percentageWidth ?? 100}px`,
              }}
            >
              <Image
                alt="before"
                src={beforeImage?.src ?? ""}
                draggable="false"
                fill
              />
              <div className={styles.labelVariants["before"]}>
                <Typography as="p" variant="smallprint-md-bold">
                  Before
                </Typography>
              </div>
            </div>
          </div>
          <div
            role="slider"
            className={styles.sliderThumb}
            style={{ left: `${sliderValue}%` }}
          >
            <div ref={sliderThumbRef} className={styles.hitBoxWrapper}>
              <span className={styles.hitBox}></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
